<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { debounce } from '@chatwoot/utils';
import DatePicker from 'vue-datepicker-next';
import ContactAPI from 'dashboard/api/contacts';
import CompanyAPI from 'dashboard/api/companies';
import DealsAPI from 'dashboard/api/dealsApi';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TaskRecurrenceField from 'dashboard/components-next/Tasks/TaskRecurrenceField.vue';
import TaskEndTimeField from 'dashboard/components-next/Tasks/TaskEndTimeField.vue';
import { buildEndDate, isEndAfterStart } from 'dashboard/helper/taskRecurrence';

const props = defineProps({
  isLoading: { type: Boolean, default: false },
  initialContactId: { type: Number, default: null },
  initialCompanyId: { type: Number, default: null },
  initialDealId: { type: Number, default: null },
  initialTitle: { type: String, default: '' },
  conversationId: { type: Number, default: null },
  initialAssignedToId: { type: Number, default: null },
});

const emit = defineEmits(['create', 'close']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

const LINK_TYPES = ['contact', 'company', 'deal'];

const form = reactive({
  title: '',
  description: '',
  assignedToId: null,
  dueDate: null,
  endTime: null,
  allDay: false,
  recurrenceRule: null,
  linkType: 'contact',
  contactId: null,
  companyId: null,
  dealId: null,
});

const agentOptions = computed(() =>
  (store.getters['agents/getAgents'] || []).map(a => ({
    value: a.id,
    label: a.name,
  }))
);

const contactOptions = ref([]);
const companyOptions = ref([]);
const dealOptions = ref([]);

const linkTypeOptions = LINK_TYPES.map(value => ({
  value,
  label: t(`TASKS.CREATE.FIELDS.LINK_TYPE_${value.toUpperCase()}`),
}));

const linkValueOptions = computed(() => {
  if (form.linkType === 'contact') return contactOptions.value;
  if (form.linkType === 'company') return companyOptions.value;
  return dealOptions.value;
});

const linkValue = computed({
  get: () => {
    if (form.linkType === 'contact') return form.contactId;
    if (form.linkType === 'company') return form.companyId;
    return form.dealId;
  },
  set: value => {
    if (form.linkType === 'contact') form.contactId = value;
    else if (form.linkType === 'company') form.companyId = value;
    else form.dealId = value;
  },
});

const validationRules = {
  title: { required },
  assignedToId: { required },
  dueDate: { required },
  endTime: {
    required: () => form.allDay || !!form.endTime,
    afterStart: () =>
      form.allDay || isEndAfterStart(form.dueDate, form.endTime),
  },
};

const v$ = useVuelidate(validationRules, form);

const titleHasError = computed(() => v$.value.title.$error);
const assignedToHasError = computed(() => v$.value.assignedToId.$error);
const dueDateHasError = computed(() => v$.value.dueDate.$error);
const endTimeHasError = computed(() => v$.value.endTime.$error);
const contactToOption = c => ({
  value: c.id,
  label: c.name || c.email || String(c.id),
});
const companyToOption = c => ({ value: c.id, label: c.name || String(c.id) });
const dealToOption = d => ({ value: d.id, label: d.name || String(d.id) });

const loadContacts = async () => {
  const {
    data: { payload },
  } = await ContactAPI.get(1, 'name');
  contactOptions.value = payload.map(contactToOption);
};

const loadCompanies = async () => {
  const {
    data: { payload },
  } = await CompanyAPI.get({ page: 1, sort: 'name' });
  companyOptions.value = payload.map(companyToOption);
};

const loadDeals = async () => {
  const {
    data: { payload },
  } = await DealsAPI.get({ page: 1 });
  dealOptions.value = payload.map(dealToOption);
};

const searchContacts = debounce(async query => {
  if (!query) {
    await loadContacts();
    return;
  }
  const {
    data: { payload },
  } = await ContactAPI.search(query, 1, 'name');
  contactOptions.value = payload.map(contactToOption);
}, 300);

const searchCompanies = debounce(async query => {
  if (!query) {
    await loadCompanies();
    return;
  }
  const {
    data: { payload },
  } = await CompanyAPI.search(query, 1, 'name');
  companyOptions.value = payload.map(companyToOption);
}, 300);

const searchDeals = debounce(async query => {
  await DealsAPI.get({ page: 1, search: query || undefined }).then(
    ({ data: { payload } }) => {
      dealOptions.value = payload.map(dealToOption);
    }
  );
}, 300);

const ensureOptionPresent = (options, id, label) => {
  if (!id || options.value.some(option => option.value === id)) return;
  options.value = [{ value: id, label: label || String(id) }, ...options.value];
};

const resetForm = () => {
  form.title = '';
  form.description = '';
  form.assignedToId = null;
  form.dueDate = null;
  form.endTime = null;
  form.allDay = false;
  form.recurrenceRule = null;
  form.linkType = 'contact';
  form.contactId = null;
  form.companyId = null;
  form.dealId = null;
  contactOptions.value = [];
  companyOptions.value = [];
  dealOptions.value = [];
  v$.value.$reset();
};

const openDialog = async (prefillDate = null, prefillEndTime = null) => {
  dialogRef.value?.open();

  if (prefillDate) form.dueDate = prefillDate;
  if (prefillEndTime) form.endTime = prefillEndTime;
  if (props.initialTitle) form.title = props.initialTitle;

  if (props.initialContactId) {
    form.linkType = 'contact';
    form.contactId = props.initialContactId;
  } else if (props.initialCompanyId) {
    form.linkType = 'company';
    form.companyId = props.initialCompanyId;
  } else if (props.initialDealId) {
    form.linkType = 'deal';
    form.dealId = props.initialDealId;
  }

  await Promise.all([
    loadContacts(),
    loadCompanies(),
    loadDeals(),
    store.dispatch('agents/get'),
  ]);

  const assigneeIsAgent = agentOptions.value.some(
    agent => agent.value === props.initialAssignedToId
  );
  if (assigneeIsAgent && form.assignedToId === null) {
    form.assignedToId = props.initialAssignedToId;
  }

  if (props.initialContactId) {
    const {
      data: { payload: contact },
    } = await ContactAPI.show(props.initialContactId);
    ensureOptionPresent(
      contactOptions,
      props.initialContactId,
      contact?.name || contact?.email
    );
  } else if (props.initialCompanyId) {
    const {
      data: { payload: company },
    } = await CompanyAPI.show(props.initialCompanyId);
    ensureOptionPresent(companyOptions, props.initialCompanyId, company?.name);
  } else if (props.initialDealId) {
    const {
      data: { payload: deal },
    } = await DealsAPI.show(props.initialDealId);
    ensureOptionPresent(dealOptions, props.initialDealId, deal?.name);
  }
};

const handleConfirm = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  emit('create', {
    title: form.title.trim(),
    description: form.description || null,
    assigned_to_id: form.assignedToId,
    due_date: form.dueDate,
    end_date: form.allDay ? null : buildEndDate(form.dueDate, form.endTime),
    all_day: form.allDay,
    recurrence_rule: form.recurrenceRule,
    contact_id: form.contactId,
    company_id: form.companyId,
    deal_id: form.dealId,
    conversation_id: props.conversationId || null,
  });
};

const handleClose = () => {
  resetForm();
  emit('close');
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const onSuccess = () => {
  resetForm();
  closeDialog();
};

defineExpose({ dialogRef, onSuccess, openDialog });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    overflow-y-auto
    :title="t('TASKS.CREATE.TITLE')"
    @confirm="handleConfirm"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="form.title"
        :label="t('TASKS.CREATE.FIELDS.TITLE')"
        :placeholder="t('TASKS.CREATE.FIELDS.TITLE_PLACEHOLDER')"
        :message="titleHasError ? t('TASKS.CREATE.FIELDS.TITLE_ERROR') : ''"
        :message-type="titleHasError ? 'error' : 'info'"
        :disabled="isLoading"
        autofocus
        @input="v$.title.$touch()"
        @blur="v$.title.$touch()"
      />

      <Input
        v-model="form.description"
        :label="t('TASKS.CREATE.FIELDS.DESCRIPTION')"
        :placeholder="t('TASKS.CREATE.FIELDS.DESCRIPTION_PLACEHOLDER')"
        :disabled="isLoading"
      />

      <div class="flex flex-col gap-1">
        <span class="mb-0.5 text-heading-3 text-n-slate-12">{{
          t('TASKS.CREATE.FIELDS.ASSIGNED_TO')
        }}</span>
        <ComboBox
          v-model="form.assignedToId"
          :options="agentOptions"
          :placeholder="t('TASKS.CREATE.FIELDS.ASSIGNED_TO_PLACEHOLDER')"
          :has-error="assignedToHasError"
          :message="
            assignedToHasError ? t('TASKS.CREATE.FIELDS.ASSIGNED_TO_ERROR') : ''
          "
          @update:model-value="v$.assignedToId.$touch()"
        />
      </div>

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="flex flex-col gap-1">
          <span class="mb-0.5 text-heading-3 text-n-slate-12">{{
            t('TASKS.CREATE.FIELDS.DUE_DATE')
          }}</span>
          <DatePicker
            v-model:value="form.dueDate"
            :type="form.allDay ? 'date' : 'datetime'"
            :show-second="false"
            :placeholder="t('TASKS.CREATE.FIELDS.DUE_DATE_PLACEHOLDER')"
            input-class="mx-input"
            :append-to-body="false"
            @change="v$.dueDate.$touch()"
          />
          <span v-if="dueDateHasError" class="text-xs text-n-ruby-9">
            {{ t('TASKS.CREATE.FIELDS.DUE_DATE_ERROR') }}
          </span>
        </div>

        <TaskEndTimeField
          v-if="!form.allDay"
          v-model="form.endTime"
          :has-error="endTimeHasError"
          @update:model-value="v$.endTime.$touch()"
        />
      </div>

      <div class="flex items-center gap-2">
        <Switch v-model="form.allDay" />
        <span class="text-sm text-n-slate-12">{{
          t('TASKS.CREATE.FIELDS.ALL_DAY')
        }}</span>
      </div>

      <TaskRecurrenceField
        v-model="form.recurrenceRule"
        :due-date="form.dueDate"
      />

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="flex flex-col gap-1">
          <span class="mb-0.5 text-heading-3 text-n-slate-12">{{
            t('TASKS.CREATE.FIELDS.LINK_TYPE')
          }}</span>
          <ComboBox v-model="form.linkType" :options="linkTypeOptions" />
        </div>

        <div class="flex flex-col gap-1">
          <span class="mb-0.5 text-heading-3 text-n-slate-12">{{
            t('TASKS.CREATE.FIELDS.LINK_VALUE')
          }}</span>
          <ComboBox
            v-model="linkValue"
            :options="linkValueOptions"
            :placeholder="t('TASKS.CREATE.FIELDS.LINK_VALUE_PLACEHOLDER')"
            :search-placeholder="
              t('TASKS.CREATE.FIELDS.LINK_VALUE_PLACEHOLDER')
            "
            @search="
              form.linkType === 'contact'
                ? searchContacts($event)
                : form.linkType === 'company'
                  ? searchCompanies($event)
                  : searchDeals($event)
            "
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center w-full gap-3">
        <Button
          :label="t('TASKS.CREATE.ACTIONS.SAVE')"
          color="blue"
          type="submit"
          :disabled="isLoading"
          :is-loading="isLoading"
        />
        <Button
          :label="t('TASKS.CREATE.ACTIONS.CANCEL')"
          variant="faded"
          color="slate"
          type="reset"
          @click="closeDialog"
        />
      </div>
    </template>
  </Dialog>
</template>
